import PinDropOutlinedIcon from '@mui/icons-material/PinDropOutlined'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import { styled, useTheme } from '@mui/material/styles'
import Tooltip from '@mui/material/Tooltip'
import React from 'react'

import { getCanSeeMoreDetails } from 'components/customer-details/content/work-orders/utils'
// eslint-disable-next-line import/no-cycle
import { convertDateTimeToPreferedTimezone } from 'data/dateTime/convertDateTimeToPreferedTimezone'
import { WORK_ORDER_STATUS, WORK_ORDER_STATUS_DISPLAY } from 'settings/constants/service'
import T from 'T'
import { ALPHA_38_PERCENT } from 'theme/colors'
import { resolveDisplayedStatus } from 'utils/workOrderStatus'

import SkipReasonTooltip from './SkipReasonTooltip'
import { getWorkOrderStatusIcon, hasSplitStatusIcon } from './workOrderStatusIcon'

const { HOLD, SCHEDULED, SKIPPED, VACATION_HOLD } = WORK_ORDER_STATUS
const CHIP_SX = { color: 'background.paper', width: 12, height: 12 }
const HOLD_ICON_SX = { color: 'background.paper', width: 14, height: 14 }
const AVATAR_SX = { width: 18, height: 18 }
const isHold = (value?: string) => value === HOLD || value === VACATION_HOLD
const iconSx = (value?: string) => {
  if (hasSplitStatusIcon(value)) return AVATAR_SX
  return isHold(value) ? HOLD_ICON_SX : CHIP_SX
}

const StyledChip = styled(Chip)(({ theme }) => ({
  paddingTop: theme.spacing(0.25),
  paddingBottom: theme.spacing(0.25),
  height: '100%',
  display: 'flex',
  '& .MuiChip-label': { overflowWrap: 'break-word', whiteSpace: 'normal', textOverflow: 'clip' },
}))

type Props = {
  status?: string
  servicedAt?: string
  servicedBy?: string
  compact?: boolean
  isAutomatic?: boolean
  // WorkOrderIntentStatus as the backend sends it; Standard and the lifecycle values render nothing.
  intentStatus?: string
  // Lets a Skipped chip show why it was skipped on hover.
  workOrderId?: string | null
  // Shown on a Skipped chip when there is no intent to name, since its tooltip replaces the caller's.
  fallbackTitle?: string
}

const WorkOrderStatusChip: React.FC<Props> = ({
  status: rawStatus,
  servicedAt = '',
  servicedBy = '',
  compact = false,
  isAutomatic = false,
  intentStatus = '',
  workOrderId,
  fallbackTitle,
}) => {
  const theme = useTheme()
  const {
    status,
    completion: completionLabel,
    intent: rawIntentLabel,
    sideIntent,
    sideCompletion,
  } = resolveDisplayedStatus(rawStatus, intentStatus)
  // Whichever dimension doesn't lead rides beside the label as a glyph.
  const sideLabel = sideIntent || sideCompletion
  const servicedAtStamp = servicedAt ? convertDateTimeToPreferedTimezone({ dateTime: servicedAt }) : ''
  // The selector answers '' when it has nothing to format, so narrow before reading the parts.
  const { onlyDate, onlyTime } = typeof servicedAtStamp === 'string' ? { onlyDate: '', onlyTime: '' } : servicedAtStamp

  // Keyed on the label rather than the status, so a kept completion beside Canceled still carries its stamp.
  const withCompletionDetail = (label: string) => {
    const canSeeMoreDetails = getCanSeeMoreDetails(label)
    return `${label}${canSeeMoreDetails && servicedAt ? ` on: ${onlyDate} @ ${onlyTime}` : ''}${
      canSeeMoreDetails && servicedBy ? ` by ${servicedBy}` : ''
    }`
  }
  const detailedLabel = withCompletionDetail(status ?? '')

  const compactLabel = `${status}${servicedAt ? ` | ${onlyTime}` : ''}`
  const avatarBackground = `${theme.palette.background.black}${ALPHA_38_PERCENT}`
  // The chip's background is the leading dimension, so the side glyph keeps its own colour.
  const sideBackground = hasSplitStatusIcon(sideLabel)
    ? 'transparent'
    : WORK_ORDER_STATUS_DISPLAY[sideLabel]?.borderColor ?? avatarBackground

  const label =
    compact || sideLabel ? (
      <Box alignItems="center" display="flex" gap={0.5}>
        <span>{compact ? compactLabel : detailedLabel}</span>
        {isAutomatic && <PinDropOutlinedIcon fontSize="inherit" />}
        {sideLabel && (
          <Avatar sx={{ ...AVATAR_SX, backgroundColor: sideBackground }}>{getWorkOrderStatusIcon(sideLabel, iconSx(sideLabel))}</Avatar>
        )}
      </Box>
    ) : (
      detailedLabel
    )

  const icon = getWorkOrderStatusIcon(status, iconSx(status))

  const chip = (
    <StyledChip
      avatar={
        icon ? <Avatar sx={{ backgroundColor: hasSplitStatusIcon(status) ? 'transparent' : avatarBackground }}>{icon}</Avatar> : undefined
      }
      label={label}
      size="small"
      sx={{
        backgroundColor: status ? WORK_ORDER_STATUS_DISPLAY[status]?.bgColor : undefined,
        ...((isHold(status) || isHold(sideIntent)) && { '& .MuiChip-avatar': { color: 'background.paper' } }),
      }}
    />
  )

  const intentTitle = rawIntentLabel && (
    <>
      <Box>{`${T.USER_INTENT_STATUS}: ${rawIntentLabel}`}</Box>
      <Box>{`${T.COMPLETION_STATUS}: ${withCompletionDetail(completionLabel || SCHEDULED)}`}</Box>
    </>
  )

  if (status === SKIPPED && workOrderId) {
    return (
      <SkipReasonTooltip header={intentTitle || fallbackTitle} workOrderId={workOrderId}>
        {chip}
      </SkipReasonTooltip>
    )
  }
  // The intent glyph is meaningless on its own, so it only ships with the tooltip that names it.
  if (!intentTitle) return chip
  return (
    <Tooltip arrow disableInteractive title={intentTitle}>
      {chip}
    </Tooltip>
  )
}

export default WorkOrderStatusChip
